import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { Audio as ExpoAudio, AVPlaybackStatus } from 'expo-av';
import { Audiobook, AudiobookChapter, getAudiobookBySlug, getPlayerDetail } from '../api/audiobooks';
import { saveRecentlyViewed } from '../utils/recentlyViewed';
import { isLoggedIn } from '../api/auth';
import { markViewed, saveProgress } from '../api/library';
import { narratorName } from '../utils/audiobookPeople';

export type PlaybackParams = {
  id?: number;
  slug?: string;
};

type PlaybackContextValue = {
  book: Audiobook | null;
  loading: boolean;
  isPlaying: boolean;
  minimized: boolean;
  position: number;
  duration: number;
  progress: number;
  title: string;
  chapterTitle: string;
  author: string;
  cover: string | null;
  chapters: AudiobookChapter[];
  activeChapterIndex: number | null;
  hasChapters: boolean;
  params?: PlaybackParams;
  load: (params?: PlaybackParams) => void;
  togglePlay: () => void;
  seekBy: (seconds: number) => void;
  seekToRatio: (ratio: number) => void;
  selectChapter: (index: number) => void;
  nextChapter: () => void;
  previousChapter: () => void;
  setMinimized: (value: boolean) => void;
  stop: () => void;
};

const PlaybackContext = createContext<PlaybackContextValue | null>(null);

function authorName(book: Audiobook | null) {
  return narratorName(book);
}

export function PlaybackProvider({ children }: { children: ReactNode }) {
  const [book, setBook] = useState<Audiobook | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [position, setPosition] = useState(0);
  const [nativeDuration, setNativeDuration] = useState(0);
  const [activeChapterIndex, setActiveChapterIndex] = useState<number | null>(null);
  const [params, setParams] = useState<PlaybackParams | undefined>();
  const webAudioRef = useRef<HTMLAudioElement | null>(null);
  const nativeSoundRef = useRef<ExpoAudio.Sound | null>(null);
  const shouldPlayRef = useRef(false);

  const chapters = book?.chapters ?? [];
  const hasChapters = chapters.length > 0;
  const firstChapter = chapters[0];
  const activeChapter = activeChapterIndex !== null ? chapters[activeChapterIndex] : undefined;
  const title = book?.name ?? '';
  const chapterTitle = activeChapter?.name ?? '';
  const author = authorName(book);
  const cover = book?.cover ?? firstChapter?.cover ?? null;
  const audioFile = activeChapter?.audio_file ?? (!hasChapters ? book?.audio_file : null) ?? null;
  const apiDuration = activeChapter?.duration ?? (!hasChapters ? book?.duration : null) ?? 0;
  const duration = nativeDuration || apiDuration;
  const progress = duration > 0 ? Math.min(position / duration, 1) : 0;

  const load = (nextParams?: PlaybackParams) => {
    setParams(nextParams);
    setLoading(true);
    setMinimized(false);
    setPosition(0);
    setActiveChapterIndex(null);

    const request = nextParams?.id
      ? getPlayerDetail(nextParams.id)
      : nextParams?.slug
        ? getAudiobookBySlug(nextParams.slug)
        : null;

    if (!request) {
      setBook(null);
      setIsPlaying(false);
      setLoading(false);
      return;
    }

    request
      .then((data) => {
        setBook(data);
        setActiveChapterIndex(data.chapters?.length ? 0 : null);
        setIsPlaying(true);
      })
      .catch(() => {
        setBook(null);
        setIsPlaying(false);
      })
      .finally(() => setLoading(false));
  };

  const stop = () => {
    if (webAudioRef.current) {
      webAudioRef.current.pause();
      webAudioRef.current.currentTime = 0;
    }

    if (nativeSoundRef.current) {
      nativeSoundRef.current.unloadAsync().catch(() => null);
      nativeSoundRef.current = null;
    }

    setBook(null);
    setParams(undefined);
    setIsPlaying(false);
    setMinimized(false);
    setPosition(0);
    setNativeDuration(0);
    setActiveChapterIndex(null);
    setLoading(false);
  };

  const seekTo = (nextPosition: number) => {
    const clamped = Math.min(Math.max(nextPosition, 0), duration || 0);
    setPosition(clamped);
    if (webAudioRef.current) {
      webAudioRef.current.currentTime = clamped;
    }

    if (nativeSoundRef.current) {
      nativeSoundRef.current.setPositionAsync(Math.round(clamped * 1000)).catch(() => null);
    }
  };

  const seekBy = (seconds: number) => {
    seekTo(position + seconds);
  };

  const seekToRatio = (ratio: number) => {
    seekTo(Math.round(Math.min(Math.max(ratio, 0), 1) * duration));
  };

  const togglePlay = () => {
    if (book) {
      setIsPlaying((value) => !value);
    }
  };

  const selectChapter = (index: number) => {
    if (!chapters[index]) {
      return;
    }

    setActiveChapterIndex(index);
    setPosition(0);
    setIsPlaying(true);
  };

  const nextChapter = () => {
    if (!hasChapters) {
      seekBy(30);
      return;
    }

    const currentIndex = activeChapterIndex ?? -1;
    selectChapter(Math.min(currentIndex + 1, chapters.length - 1));
  };

  const previousChapter = () => {
    if (!hasChapters) {
      seekBy(-30);
      return;
    }

    const currentIndex = activeChapterIndex ?? 0;
    selectChapter(Math.max(currentIndex - 1, 0));
  };

  useEffect(() => {
    shouldPlayRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    ExpoAudio.setAudioModeAsync({
      allowsRecordingIOS: false,
      playsInSilentModeIOS: true,
      shouldDuckAndroid: true,
      staysActiveInBackground: false,
    }).catch(() => null);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof Audio === 'undefined' || !audioFile) {
      webAudioRef.current = null;
      return undefined;
    }

    const audio = new Audio(audioFile);
    audio.preload = 'metadata';
    webAudioRef.current = audio;

    const updateTime = () => setPosition(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('ended', handleEnded);
      if (webAudioRef.current === audio) {
        webAudioRef.current = null;
      }
    };
  }, [audioFile]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return undefined;
    }

    let mounted = true;
    setNativeDuration(0);

    const unloadCurrent = async () => {
      const current = nativeSoundRef.current;
      nativeSoundRef.current = null;
      if (current) {
        await current.unloadAsync().catch(() => null);
      }
    };

    if (!audioFile) {
      unloadCurrent();
      return undefined;
    }

    unloadCurrent()
      .then(() => ExpoAudio.Sound.createAsync(
        { uri: audioFile },
        { shouldPlay: false, progressUpdateIntervalMillis: 500 },
        (status: AVPlaybackStatus) => {
          if (!mounted || !status.isLoaded) {
            return;
          }

          setPosition(status.positionMillis / 1000);
          if (status.durationMillis) {
            setNativeDuration(status.durationMillis / 1000);
          }
          if (status.didJustFinish) {
            setIsPlaying(false);
          }
        },
      ))
      .then(({ sound }) => {
        if (!mounted) {
          sound.unloadAsync().catch(() => null);
          return;
        }
        nativeSoundRef.current = sound;
        if (shouldPlayRef.current) {
          sound.playAsync().catch(() => setIsPlaying(false));
        }
      })
      .catch(() => {
        if (mounted) {
          setIsPlaying(false);
        }
      });

    return () => {
      mounted = false;
      unloadCurrent();
    };
  }, [audioFile]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const audio = webAudioRef.current;
      if (!audio) {
        return;
      }

      if (isPlaying) {
        audio.play().catch(() => setIsPlaying(false));
      } else {
        audio.pause();
      }

      return;
    }

    const sound = nativeSoundRef.current;
    if (!sound) {
      return;
    }

    if (isPlaying) {
      sound.playAsync().catch(() => setIsPlaying(false));
    } else {
      sound.pauseAsync().catch(() => null);
    }
  }, [isPlaying, audioFile]);

  useEffect(() => {
    if (webAudioRef.current || nativeSoundRef.current || !isPlaying || !duration) {
      return undefined;
    }

    const timer = setInterval(() => {
      setPosition((current) => {
        const next = Math.min(current + 1, duration);
        if (next >= duration) {
          setIsPlaying(false);
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [duration, isPlaying]);

  useEffect(() => {
    if (!book) {
      return;
    }

    saveRecentlyViewed({
      id: book.id,
      slug: book.slug,
      title,
      chapterTitle,
      author,
      cover,
      position,
      duration,
    });

    if (!isLoggedIn()) {
      return;
    }

    if (position <= 1) {
      markViewed(book.id).catch(() => null);
      return;
    }

    if (Math.round(position) % 10 === 0) {
      saveProgress(book.id, position, duration, activeChapter?.id).catch(() => null);
    }
  }, [activeChapter?.id, author, book, chapterTitle, cover, duration, position, title]);

  const value = useMemo<PlaybackContextValue>(() => ({
    book,
    loading,
    isPlaying,
    minimized,
    position,
    duration,
    progress,
    title,
    chapterTitle,
    author,
    cover,
    chapters,
    activeChapterIndex,
    hasChapters,
    params,
    load,
    togglePlay,
    seekBy,
    seekToRatio,
    selectChapter,
    nextChapter,
    previousChapter,
    setMinimized,
    stop,
  }), [activeChapterIndex, author, book, chapterTitle, chapters, cover, duration, hasChapters, isPlaying, loading, minimized, params, position, progress, title]);

  return (
    <PlaybackContext.Provider value={value}>
      {children}
    </PlaybackContext.Provider>
  );
}

export function usePlayback() {
  const value = useContext(PlaybackContext);
  if (!value) {
    throw new Error('usePlayback must be used inside PlaybackProvider');
  }

  return value;
}
